import {
  type CreateParticipantRestrictivePracticeRequest,
  RestrictivePracticeType,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { DateTime, dateTimeSchema, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useCreateParticipantRestrictivePracticeMutation } from '../../../graphql/hooks/useCreateParticipantRestrictivePracticeMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantRestrictivePracticeForm } from './ParticipantRestrictivePracticeForm';

type FormValues = CreateParticipantRestrictivePracticeRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  description: z.string().nonempty('Please enter a description'),
  type: z.enum(RestrictivePracticeType),
  identifiedAt: dateTimeSchema,
  startedAt: dateTimeSchema.optional().nullable(),
  ceasedAt: dateTimeSchema.optional().nullable(),
  labelIds: z.array(z.string()),
});

interface Props {
  participantId: string;
}

export const ParticipantRestrictivePracticeCreateModal = createModal<Props>((props) => {
  const { modal } = props;
  const { remove, hide, visible } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useCreateParticipantRestrictivePracticeMutation();

  const methods = useForm<FormValues>({
    defaultValues: {
      description: '',
      identifiedAt: DateTime.now(),
      startedAt: null,
      ceasedAt: null,
      type: RestrictivePracticeType.ChemicalRestraint,
      labelIds: [],
    },
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId: props.participantId,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Participant restrictive practice created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating participant restrictive practice: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, props.participantId, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a participant restrictive practice'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantRestrictivePracticeForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
